import { ProductsRepository } from '@lib/api/repository/products/products.repository';
import {
  CreateProductInput,
  CreateProductOfferInput,
  Product,
  ProductOffer,
  UpdateProductOfferInput,
} from '@lib/types';

import { Requester } from '../clients';

export interface ProductDTO {
  createdAt?: string;
  updatedAt?: string;
  id: string;
  game: { id: string; name: string; slug: string } | null;
  name: string;
  slug: string;
  description: string;
  category: Product['category'];
  isPlayable: boolean;
  manufacturer?: string | null;
  status: Product['status'];
  primaryImage?: { imageUrl: string; altText: string } | null;
  widthCm?: number | null;
  heightCm?: number | null;
  lengthCm?: number | null;
  minimumPrice?: string | null;
  originalMinimumPrice?: string | null;
  availableQuantity: number;
}

export interface ProductOfferDTO {
  id: string;
  product: ProductDTO;
  store: { id: string; name: string; slug: string };
  price: string;
  originalPrice?: string | null;
  promotionEndsAt?: string;
  imageUrl?: string | null;
  sellerBarcode?: string | null;
  sellerSku?: string | null;
  widthCm?: number | null;
  heightCm?: number | null;
  lengthCm?: number | null;
  quantity: number;
  condition: ProductOffer['condition'];
  status: ProductOffer['status'];
}

export const createProduct = (dto: ProductDTO): Product => ({
  id: dto.id,
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
  game: dto.game,
  name: dto.name,
  slug: dto.slug,
  description: dto.description,
  category: dto.category,
  isPlayable: dto.isPlayable,
  manufacturer: dto.manufacturer,
  status: dto.status,
  primaryImage: dto.primaryImage
    ? {
        imageUrl: dto.primaryImage.imageUrl,
        altText: dto.primaryImage.altText,
      }
    : null,
  widthCm: dto.widthCm,
  heightCm: dto.heightCm,
  lengthCm: dto.lengthCm,
  minimumPrice: dto.minimumPrice,
  originalMinimumPrice: dto.originalMinimumPrice,
  availableQuantity: dto.availableQuantity,
});

export const createProductOffer = (dto: ProductOfferDTO): ProductOffer => ({
  id: dto.id,
  product: createProduct(dto.product),
  store: dto.store,
  price: dto.price,
  originalPrice: dto.originalPrice,
  promotionEndsAt: dto.promotionEndsAt,
  imageUrl: dto.imageUrl,
  sellerBarcode: dto.sellerBarcode,
  sellerSku: dto.sellerSku,
  widthCm: dto.widthCm,
  heightCm: dto.heightCm,
  lengthCm: dto.lengthCm,
  quantity: dto.quantity,
  condition: dto.condition,
  status: dto.status,
});

const productFormData = (input: CreateProductInput) => {
  const body = input.imageFile
    ? new FormData()
    : {
        price: input.price,
        quantity: input.quantity,
        condition: input.condition,
        seller_sku: input.sellerSku,
        seller_barcode: input.sellerBarcode,
        game_id: input.gameId,
        collection_id: input.collectionId,
        name: input.name,
        description: input.description,
        category: input.category,
        manufacturer: input.manufacturer,
        gtin: input.gtin,
        weight_grams: input.weightGrams,
        height_cm: input.heightCm,
        width_cm: input.widthCm,
        length_cm: input.lengthCm,
        images: input.images.map((image) => ({
          image_url: image.imageUrl,
          alt_text: image.altText,
        })),
      };

  if (body instanceof FormData) {
    if (input.gameId) {
      body.append('game_id', input.gameId);
    }
    if (input.price !== undefined) {
      body.append('price', String(input.price));
    }
    if (input.quantity !== undefined) {
      body.append('quantity', String(input.quantity));
    }
    body.append('name', input.name);
    body.append('description', input.description);
    body.append('category', input.category);
    body.append('width_cm', String(input.widthCm));
    body.append('height_cm', String(input.heightCm));
    body.append('length_cm', String(input.lengthCm));
    if (input.condition) {
      body.append('condition', input.condition);
    }
    if (input.sellerSku?.trim()) {
      body.append('seller_sku', input.sellerSku.trim());
    }
    if (input.sellerBarcode?.trim()) {
      body.append('seller_barcode', input.sellerBarcode.trim());
    }
    if (input.manufacturer) {
      body.append('manufacturer', input.manufacturer);
    }
    if (input.imageFile) {
      body.append('file', input.imageFile);
    }
  }

  return body;
};

export default class ProductsAPI implements ProductsRepository {
  async updateProduct(id: string, input: CreateProductInput) {
    const response = await Requester.patch<ProductOfferDTO>(
      `/marketplace/products/offers/${id}/product`,
      { body: productFormData(input) },
    );

    return createProductOffer(response.data);
  }

  async deleteOffer(id: string) {
    await Requester.delete(`/marketplace/products/offers/${id}`);
  }

  async findStoreOffer(slug: string, id: string, signal?: AbortSignal) {
    const response = await Requester.get<ProductOfferDTO>(
      `/marketplace/products/stores/${encodeURIComponent(slug)}/offers/${encodeURIComponent(id)}`,
      { signal },
    );

    return createProductOffer(response.data);
  }

  async listStoreOffers(
    slug: string,
    page: number,
    signal?: AbortSignal,
    owner?: boolean,
  ) {
    const response = await Requester.get<{
      items: ProductOfferDTO[];
      total: number;
    }>(
      `/marketplace/products/stores/${encodeURIComponent(slug)}/${owner ? 'owner-offers' : 'offers'}`,
      {
        signal,
        params: { page },
      },
    );

    return {
      items: response.data.items.map(createProductOffer),
      total: response.data.total,
    };
  }

  async list(
    filters?: {
      gameCode?: string;
      category?: string;
      search?: string;
    },
    signal?: AbortSignal,
  ): Promise<Product[]> {
    const response = await Requester.get<ProductDTO[]>(
      '/marketplace/products',
      { signal, params: filters },
    );

    return response.data.map(createProduct);
  }

  async findBySlug(
    gameCode: string,
    slug: string,
    signal?: AbortSignal,
  ): Promise<Product> {
    const response = await Requester.get<ProductDTO>(
      `/marketplace/products/${gameCode}/${slug}`,
      { signal },
    );

    return createProduct(response.data);
  }

  async propose(input: CreateProductInput): Promise<Product> {
    const body = productFormData(input);

    const response = await Requester.post<ProductDTO>(
      '/marketplace/products/propose',
      { body },
    );

    return createProduct(response.data);
  }

  async listAdmin(page: number, signal?: AbortSignal) {
    const response = await Requester.get<{
      items: ProductDTO[];
      total: number;
    }>('/marketplace/products/admin', { signal, params: { page } });

    return {
      items: response.data.items.map(createProduct),
      total: response.data.total,
    };
  }

  async setStatus(id: string, status: 'active' | 'paused') {
    const response = await Requester.put<ProductDTO>(
      `/marketplace/products/admin/${id}/status`,
      { body: { status } },
    );

    return createProduct(response.data);
  }

  async listMyOffers(signal?: AbortSignal): Promise<ProductOffer[]> {
    const response = await Requester.get<ProductOfferDTO[]>(
      '/marketplace/products/offers/mine',
      { signal },
    );

    return response.data.map(createProductOffer);
  }

  async createOffer(input: CreateProductOfferInput): Promise<ProductOffer> {
    const body = new FormData();

    body.append('product_id', input.productId);
    body.append('price', String(input.price));
    body.append('quantity', String(input.quantity));
    body.append('condition', input.condition);
    body.append('seller_barcode', input.sellerBarcode);
    body.append('width_cm', String(input.widthCm));
    body.append('height_cm', String(input.heightCm));
    body.append('length_cm', String(input.lengthCm));
    if (input.variantId) {
      body.append('variant_id', input.variantId);
    }
    if (input.sellerSku) {
      body.append('seller_sku', input.sellerSku);
    }
    if (input.clientReference) {
      body.append('client_reference', input.clientReference);
    }
    if (input.imageFile) {
      body.append('file', input.imageFile);
    }

    const response = await Requester.post<ProductOfferDTO>(
      '/marketplace/products/offers',
      { body },
    );

    return createProductOffer(response.data);
  }

  async updateOffer(
    id: string,
    input: UpdateProductOfferInput,
  ): Promise<ProductOffer> {
    const response = await Requester.patch<ProductOfferDTO>(
      `/marketplace/products/offers/${id}`,
      { body: input },
    );

    return createProductOffer(response.data);
  }
}
